import { Button } from '@1trading/react/components/ui/button';
import { Checkbox } from '@1trading/react/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { format, parseISO } from 'date-fns';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingTitle } from '@/components/ui/page-heading';
import type { InstitutionAuthorisation, InstitutionAuthorisationOrDefaults } from '@/features/institution/authorisation/schema';
import { useInstitutionOnboardingBasePath, useIsInstitutionDetailsUpdate } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import {
  useInstitutionDetailsAnswers,
  useInstitutionDetailsMutation,
  useInstitutionDetailsV2Answers,
  useInstitutionDetailsVersion,
} from '@/features/institution/hooks/useInstitutionDetails';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { INSTITUTION_DETAILS_VERSION, institutionDetailsAuthorisationSchema } from '@/services/api/profile';

const InstitutionAuthorisationFormForm = ({ defaultValues, isFuturesSelected }: { defaultValues: InstitutionAuthorisationOrDefaults; isFuturesSelected: boolean }) => {
  const form = useForm<InstitutionAuthorisation>({
    defaultValues: {
      ...defaultValues,
      fullName: defaultValues?.fullName ?? '',
      date: defaultValues?.date ?? new Date().toISOString(),
    },
    resolver: standardSchemaResolver(institutionDetailsAuthorisationSchema),
  });
  const isUpdate = useIsInstitutionDetailsUpdate();
  const { isPending, mutate } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: InstitutionAuthorisation) => {
    mutate(
      { authorisation: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          // An upgrading firm may be verified already or still applying, so the initial redirect works out what it needs next
          if (isUpdate) {
            navigate('/institution/onboarding', { replace: true });

            return;
          }

          // A V2 futures application finishes its futures steps once the answers it rests on are signed
          navigate(isFuturesSelected ? '/institution/onboarding/futures/services-agreement' : '/institution/onboarding/services-agreement', { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="institution-company" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.authorisation.fullName')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.authorisation.placeholders.fullName')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => {
              const displayDate = field.value ? format(parseISO(field.value), 'yyyy-MM-dd HH:mm:ss') : '';

              return (
                <FormItem className="col-span-2">
                  <FormLabel>{t('institutionDetails.authorisation.date')}*</FormLabel>
                  <FormControl>
                    <Input {...field} value={displayDate} readOnly />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="confirmation"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center gap-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel>{t('institutionDetails.authorisation.confirmation')}</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.submit')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionAuthorisationFormForm.displayName = 'InstitutionAuthorisationFormForm';

const InstitutionAuthorisationForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'authorisation');
  const { data: version } = useInstitutionDetailsVersion();
  const { data: institutionDetailsV2Answers } = useInstitutionDetailsV2Answers();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;
  const isFuturesSelected = institutionDetailsV2Answers?.products?.futures === 'YES';

  const handlePrevStep = useCallback(() => {
    if (version === INSTITUTION_DETAILS_VERSION.V1) {
      navigate(`${onboardingPath}/regulatory-information`, { replace: true });
    } else {
      navigate(`${onboardingPath}/review`, { replace: true });
    }
  }, [navigate, version, onboardingPath]);

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.authorisation ?? {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.authorisation.title')}</PageHeadingTitle>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionAuthorisationFormForm defaultValues={defaultValues} isFuturesSelected={isFuturesSelected} />
      </PageCard>
    </section>
  );
};
InstitutionAuthorisationForm.displayName = 'InstitutionAuthorisationForm';

export default InstitutionAuthorisationForm;
